/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

import { Component, forwardRef, HostListener, Input, ElementRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

/**
 * Valueslider component voor het instellen van een numerieke waarde via een slider.
 * Qua lay-out gebaseerd op de dl-panzoom slider uit de moederapp.
 *
 * Gegenereerd met Junie (AI), 24-03-2026
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 */
@Component({
  selector: 'valueslider',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './valueslider.component.html',
  styleUrl: './valueslider.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ValuesliderComponent),
      multi: true
    }
  ]
})
export class ValuesliderComponent implements ControlValueAccessor {
  /** De minimale waarde van de slider */
  @Input() public min: number = 0;
  /** De maximale waarde van de slider */
  @Input() public max: number = 20;
  /** De minimale stapgrootte voor wijzigingen */
  @Input() public interval: number = 1;
  /** De waarde waarnaar gereset moet worden (indien opgegeven) */
  @Input() public reset?: number;

  /** Referentie naar de slider container voor muisberekeningen */
  @ViewChild('sliderContainer', { static: true }) private sliderContainer!: ElementRef<HTMLDivElement>;

  /** De huidige waarde van de slider */
  public value: number = 0;
  /** Of de gebruiker momenteel aan het slepen is */
  protected isDragging: boolean = false;
  /** De interval timer voor het herhaaldelijk aanpassen van de waarde */
  private repeatTimer?: any;

  private onChange: (value: number) => void = () => {};
  private onTouched: () => void = () => {};

  /**
   * Berekent de linker positie van de handle in percentage (0-100).
   */
  protected get handlePosition(): number {
    if (this.max <= this.min) return 0;
    const percentage = ((this.value - this.min) / (this.max - this.min)) * 100;
    return Math.max(0, Math.min(100, percentage));
  }

  /**
   * Wordt aangeroepen door Angular wanneer de waarde van buitenaf wijzigt.
   */
  public writeValue(value: number): void {
    this.value = value ?? this.min;
  }

  /**
   * Registreert de callback voor waarde-wijzigingen.
   */
  public registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  /**
   * Registreert de callback voor 'touched' status.
   */
  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Start het sleep-proces.
   */
  protected onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.updateValueFromEvent(event);
    this.onTouched();
  }

  /**
   * Werkt de waarde bij tijdens het slepen.
   */
  @HostListener('document:mousemove', ['$event'])
  protected onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      this.updateValueFromEvent(event);
    }
  }

  /**
   * Stopt het sleep-proces.
   */
  @HostListener('document:mouseup')
  protected onMouseUp(): void {
    this.isDragging = false;
  }

  /**
   * Start het verhogen van de waarde zolang de knop ingedrukt is.
   */
  protected startVerhoog(): void {
    this.verhoog();
    this.startRepeat(() => this.verhoog());
  }

  /**
   * Start het verlagen van de waarde zolang de knop ingedrukt is.
   */
  protected startVerlaag(): void {
    this.verlaag();
    this.startRepeat(() => this.verlaag());
  }

  /**
   * Stopt de herhalende actie (bijv op mouseup of mouseleave).
   */
  protected stopActie(): void {
    if (this.repeatTimer) {
      clearTimeout(this.repeatTimer);
      clearInterval(this.repeatTimer);
      this.repeatTimer = undefined;
    }
  }

  /**
   * Start een interval timer voor het herhalen van een actie.
   * @param actie De actie die herhaald moet worden.
   */
  private startRepeat(actie: () => void): void {
    this.stopActie();
    // Start met een kleine vertraging voordat het herhalen begint, vergelijkbaar met keyboard repeat
    this.repeatTimer = setTimeout(() => {
      this.repeatTimer = setInterval(() => actie(), 100);
    }, 400);
  }

  /**
   * Verhoogt de waarde met één interval.
   */
  public verhoog(): void {
    const newValue = Math.min(this.max, this.value + this.interval);
    if (this.value !== newValue) {
      this.value = newValue;
      this.onChange(this.value);
    }
  }

  /**
   * Verlaagt de waarde met één interval.
   */
  public verlaag(): void {
    const newValue = Math.max(this.min, this.value - this.interval);
    if (this.value !== newValue) {
      this.value = newValue;
      this.onChange(this.value);
    }
  }

  /**
   * Reset de waarde naar de opgegeven reset-waarde.
   */
  public resetWaarde(): void {
    if (this.reset !== undefined) {
      this.value = this.reset;
      this.onChange(this.value);
    }
  }

  /**
   * Berekent de nieuwe waarde op basis van de muispositie binnen de slider.
   */
  private updateValueFromEvent(event: MouseEvent): void {
    const rect = this.sliderContainer.nativeElement.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const width = rect.width;
    
    let percentage = x / width;
    percentage = Math.max(0, Math.min(1, percentage));
    
    let rawValue = this.min + percentage * (this.max - this.min);
    
    // Pas interval toe
    if (this.interval > 0) {
      rawValue = Math.round(rawValue / this.interval) * this.interval;
    }
    
    // Clamp waarde tussen min en max
    const newValue = Math.max(this.min, Math.min(this.max, rawValue));
    
    if (this.value !== newValue) {
      this.value = newValue;
      this.onChange(this.value);
    }
  }
}
